import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Sparkles, AlertTriangle, Share2, Copy, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { membersApi } from "@/lib/api/directory";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const TIER_STYLES = {
    SILVER:   { ring: "from-zinc-400 to-zinc-200",     label: { en: "Silver",   ar: "فضي" } },
    GOLD:     { ring: "from-amber-500 to-yellow-300",  label: { en: "Gold",     ar: "ذهبي" } },
    PLATINUM: { ring: "from-sky-500 to-indigo-300",    label: { en: "Platinum", ar: "بلاتيني" } },
};

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch (_) { return iso; }
}

function fmtExpiry(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = d.getFullYear();
        return `${mm}/${yy}`;
    } catch (_) { return "—"; }
}

function PhysicalLoyaltyCard({ member, lang }) {
    const cardNumber = member?.member_code
        ? (member.member_code.match(/.{1,4}/g) || []).join("  ")
        : "XXXX  XXXX  XXXX";

    return (
        <div
            className="relative w-full overflow-hidden rounded-2xl shadow-2xl"
            style={{
                aspectRatio: "85.6/53.98",
                background: "linear-gradient(135deg, #003F9E 0%, #0060CC 45%, #0088EE 100%)",
            }}
            data-testid="member-card"
        >
            {/* Hexagonal grid */}
            <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.08 }}>
                <defs>
                    <pattern id="hexCard" width="30" height="52" patternUnits="userSpaceOnUse">
                        <path d="M15 1 L29 9 L29 25 L15 33 L1 25 L1 9 Z" fill="none" stroke="white" strokeWidth="1" />
                        <path d="M15 33 L29 41 M15 33 L1 41" fill="none" stroke="white" strokeWidth="1" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#hexCard)" />
            </svg>

            {/* ECG heartbeat */}
            <svg
                className="absolute top-0 h-full"
                style={{ right: 0, width: "58%" }}
                viewBox="0 0 220 150"
                preserveAspectRatio="xMidYMid meet"
            >
                <path
                    d="M0,75 L25,75 L33,68 L40,75 L46,52 L56,100 L66,75 L90,75 L98,68 L105,75 L111,52 L121,100 L131,75 L155,75 L163,68 L170,75 L176,52 L186,100 L196,75 L220,75"
                    fill="none" stroke="#38B6FF" strokeWidth="6" opacity="0.3"
                />
                <path
                    d="M0,75 L25,75 L33,68 L40,75 L46,52 L56,100 L66,75 L90,75 L98,68 L105,75 L111,52 L121,100 L131,75 L155,75 L163,68 L170,75 L176,52 L186,100 L196,75 L220,75"
                    fill="none" stroke="#5DCCFF" strokeWidth="2"
                />
            </svg>

            {/* Hemaia logo — top right */}
            <div className="absolute top-4 right-5">
                <img src="/hemaia_logo_light.svg" alt="Hemaia" className="h-6 sm:h-8" />
            </div>

            {/* Card content */}
            <div className="absolute inset-0 p-5 sm:p-6 flex flex-col justify-between">
                <div />
                <div>
                    <p className="text-white/50 text-[8px] sm:text-[9px] uppercase tracking-[0.15em] font-medium mb-0.5">
                        {lang === "ar" ? "رقم البطاقة" : "Card Number"}
                    </p>
                    <p className="text-white font-mono text-base sm:text-xl tracking-[0.2em] font-bold" data-testid="member-card-code">
                        {cardNumber}
                    </p>
                </div>
                <div className="flex items-end justify-between">
                    <div className="space-y-2 sm:space-y-3">
                        <div>
                            <p className="text-white/50 text-[8px] sm:text-[9px] uppercase tracking-[0.1em] font-medium mb-0.5">
                                {lang === "ar" ? "صالحة حتى" : "Valid Thru"}
                            </p>
                            <p className="text-white text-sm sm:text-base font-semibold" data-testid="member-card-expiry">
                                {fmtExpiry(member?.expiry_date)}
                            </p>
                        </div>
                        <div>
                            <p className="text-white/50 text-[8px] sm:text-[9px] uppercase tracking-[0.1em] font-medium mb-0.5">
                                {lang === "ar" ? "اسم حامل البطاقة" : "Card Holder Name"}
                            </p>
                            <p className="text-white text-sm sm:text-base font-bold uppercase tracking-wide" data-testid="member-card-name">
                                {member?.full_name}
                            </p>
                        </div>
                    </div>
                    <div className="text-right pb-0.5">
                        <p className="text-white/60 text-[8px] sm:text-[9px] font-bold uppercase leading-tight tracking-wide">
                            {lang === "ar" ? <>شبكة الولاء<br />الصحية في مصر</> : <>Egypt's Healthcare<br />Loyalty Network</>}
                        </p>
                    </div>
                </div>
            </div>

            {/* Expired overlay */}
            {member?.is_expired && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
                    <div className="bg-destructive text-white px-5 py-2 rounded-xl text-sm font-bold uppercase tracking-widest rotate-[-6deg] shadow-xl">
                        {lang === "ar" ? "منتهية الصلاحية" : "Expired"}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function MemberCardPage() {
    const { t, lang } = useI18n();
    const [member, setMember] = useState(null);
    const [shared, setShared] = useState(false);

    useEffect(() => {
        let active = true;
        membersApi.me()
            .then((d) => { if (active) setMember(d); })
            .catch((err) => active && toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message));
        return () => { active = false; };
    }, []);

    const buildReferralLink = () => {
        if (!member) return "";
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        return `${origin}/signup?ref=${member.member_code}`;
    };

    const onShare = async () => {
        const url = buildReferralLink();
        const text = t("member.card.shareMessage", { code: member.member_code });
        try {
            if (navigator.share) {
                await navigator.share({ title: t("member.card.shareTitle"), text, url });
                return;
            }
        } catch (_) { /* user cancelled — fall back */ }
        try {
            await navigator.clipboard.writeText(`${text}\n${url}`);
            setShared(true);
            toast.success(t("member.card.shareCopied"));
            setTimeout(() => setShared(false), 2400);
        } catch (_) {
            toast.error(t("member.card.shareError"));
        }
    };

    if (!member) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-member-card-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const tierStyle = TIER_STYLES[member.tier] || TIER_STYLES.SILVER;
    const tierLabel = tierStyle.label[lang] || member.tier;

    return (
        <div className="max-w-2xl space-y-6 fade-up" data-testid="page-member-card">
            <header>
                <p className="text-sm font-medium text-primary">{t("member.card.eyebrow")}</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">
                    {t("member.card.title")}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">{t("member.card.subtitle")}</p>
            </header>

            {/* Physical Loyalty Card */}
            <PhysicalLoyaltyCard member={member} lang={lang} />

            {/* Expiry warnings */}
            {member.is_expired && (
                <div
                    className="flex items-center gap-2 rounded-xl bg-destructive/5 border border-destructive/30 px-3 py-2 text-sm text-destructive"
                    data-testid="member-card-expired-banner"
                >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{t("member.card.expiredBanner")}</span>
                </div>
            )}
            {!member.is_expired && member.is_expiring_soon && (
                <div
                    className="flex items-center gap-2 rounded-xl bg-destructive/5 border border-destructive/30 px-3 py-2 text-sm text-destructive"
                    data-testid="member-card-expiring-banner"
                >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{t("member.card.expiringSoonBanner", { days: member.days_to_expiry })}</span>
                </div>
            )}

            {/* Tier badge */}
            <div className="flex items-center gap-2">
                <div className={`rounded-full p-[2px] bg-gradient-to-br ${tierStyle.ring}`}>
                    <div
                        className="bg-card rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-wider"
                        data-testid="member-card-tier"
                    >
                        {tierLabel}
                    </div>
                </div>
                <span className="text-sm text-muted-foreground">{lang === "ar" ? "مستوى العضوية" : "Membership tier"}</span>
            </div>

            <div className="surface-card p-5">
                <div className="flex gap-3">
                    <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div className="text-sm text-muted-foreground">
                        <p className="font-semibold text-foreground mb-1">{t("member.card.howToUse")}</p>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>{t("member.card.step1")}</li>
                            <li>{t("member.card.step2")}</li>
                            <li>{t("member.card.step3")}</li>
                        </ol>
                    </div>
                </div>
            </div>

            <div className="surface-card p-5 flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">{t("member.card.familyNote")}</p>
            </div>

            {/* Share my benefit — referral */}
            <div className="surface-card p-5" data-testid="share-benefit-card">
                <div className="flex items-start gap-3">
                    <div className="icon-tile h-10 w-10 shrink-0">
                        <Share2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{t("member.card.shareTitle")}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{t("member.card.shareBody")}</p>
                    </div>
                </div>
                <Button
                    onClick={onShare}
                    className="mt-4 w-full"
                    data-testid="share-benefit-button"
                >
                    {shared ? <CheckCircle2 className="h-4 w-4 me-2" /> : <Share2 className="h-4 w-4 me-2" />}
                    {shared ? t("member.card.shareCopied") : t("member.card.shareCta")}
                </Button>
            </div>
        </div>
    );
}
